"use client";

import { useState } from "react";
import Link from "next/link";
import {
  X,
  Receipt,
  FileAudio2,
  FileVideo2,
  Paperclip,
  Loader2,
  Ban,
  ExternalLink,
  CreditCard,
} from "lucide-react";
import { ProProfileCard } from "@/components/expert-feedback/ProProfileCard";
import { getFeedbackOptionLabel } from "@/lib/feedback/feedbackOptionLabels";
import {
  canCancelFeedbackRequest,
  canRefundFeedbackRequest,
  getFeedbackStatusClass,
  getFeedbackStatusDescription,
  getFeedbackStatusLabel,
} from "@/lib/feedbackStatus";

export interface FeedbackRequestItem {
  id: string;
  userId: string;
  status: string;
  paymentStatus?: string;
  requestType: "feedback" | "analysis";
  category?: string;
  analysisCategory?: string;
  productName?: string;
  optionLabel?: string;
  feedbackOption?: number | string;
  songTitle?: string;
  fileName?: string;
  messageToExpert?: string;
  additionalRequest?: string;
  paidAmount?: number;
  originalPrice?: number;
  paymentMerchantUid?: string;
  paymentImpUid?: string;
  paymentProvider?: string;
  paymentMethod?: string;
  paymentVerifiedAt?: Date;
  requestedAt?: Date;
  createdAt?: Date;
  completedAt?: Date;
  cancelledAt?: Date;
  audioUrl?: string;
  videoUrl?: string;
  fileUrl?: string;
  audioFileName?: string;
  videoFileName?: string;
  attachmentFileName?: string;
  expertId?: string;
  expertName?: string;
  assignedProId?: string;
  assignedProName?: string;
}

function formatDateTime(value?: Date) {
  if (!value || Number.isNaN(value.getTime())) return "-";
  return value.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAmount(amount?: number) {
  if (typeof amount !== "number") return "-";
  return `${amount.toLocaleString()}원`;
}

interface FeedbackRequestDetailDrawerProps {
  request: FeedbackRequestItem;
  onClose: () => void;
  onCancelled: (requestId: string) => void;
}

export function FeedbackRequestDetailDrawer({
  request,
  onClose,
  onCancelled,
}: FeedbackRequestDetailDrawerProps) {
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const category =
    request.category || request.analysisCategory || request.productName;
  const optionLabel =
    request.optionLabel ||
    getFeedbackOptionLabel(request.feedbackOption) ||
    "프로 피드백";
  const title =
    request.songTitle || request.fileName || category || "피드백 신청";
  const message =
    request.messageToExpert || request.additionalRequest || "";
  const proId = request.expertId || request.assignedProId;
  const proName = request.expertName || request.assignedProName;
  const paymentId = request.paymentMerchantUid || request.paymentImpUid;
  const cancellable = canCancelFeedbackRequest(
    request.status,
    request.paymentStatus,
    request.assignedProId || request.expertId
  );
  const refundable = canRefundFeedbackRequest(
    request.status,
    request.paymentStatus,
    request.assignedProId || request.expertId
  );

  const handleCancel = async () => {
    const message = refundable
      ? "신청을 취소하고 결제 금액을 환불할까요?\n(프로 배정 전까지만 가능)"
      : "이 신청을 취소할까요?\n(프로 배정 전까지만 가능)";
    if (!window.confirm(message)) return;

    setCancelling(true);
    setError(null);
    try {
      const { getFirebaseAuth } = await import("@/lib/api/firebase-ultra-safe");
      const token = await getFirebaseAuth()?.currentUser?.getIdToken();
      const res = await fetch("/api/feedback/cancel-request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ requestId: request.id }),
      });
      const json = (await res.json()) as { success?: boolean; error?: string };
      if (!res.ok || !json.success) {
        throw new Error(json.error || "취소에 실패했습니다.");
      }
      onCancelled(request.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "취소에 실패했습니다.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="h-full w-full max-w-lg border-l border-gray-200 bg-white shadow-2xl overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white/95 px-5 py-4 backdrop-blur">
          <div>
            <p className="text-xs text-violet-600 font-medium">신청 상세</p>
            <h2 className="text-lg font-semibold text-gray-950 mt-0.5">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-900"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5 pb-28">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getFeedbackStatusClass(request.status)}`}
            >
              {getFeedbackStatusLabel(request.status)}
            </span>
            {request.paymentStatus === "paid" && (
              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border border-emerald-200 bg-emerald-50 text-emerald-700">
                결제 완료
              </span>
            )}
            {request.paymentStatus === "refunded" && (
              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border border-rose-200 bg-rose-50 text-rose-700">
                환불 완료
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">
            {getFeedbackStatusDescription(request.status)}
          </p>

          <section className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">신청 정보</h3>
            <dl className="grid grid-cols-[88px_1fr] gap-y-2.5 text-sm">
              <dt className="text-gray-500">신청 번호</dt>
              <dd className="text-gray-800 font-mono text-xs break-all">
                {request.id}
              </dd>
              <dt className="text-gray-500">카테고리</dt>
              <dd className="text-gray-800">{category || "-"}</dd>
              <dt className="text-gray-500">옵션</dt>
              <dd className="text-gray-800">{optionLabel}</dd>
              <dt className="text-gray-500">신청 일시</dt>
              <dd className="text-gray-800">
                {formatDateTime(request.requestedAt || request.createdAt)}
              </dd>
            </dl>
            {message && (
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                <p className="text-xs text-gray-500 mb-1">프로에게 전달한 메모</p>
                <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">
                  {message}
                </p>
              </div>
            )}
          </section>

          {(request.audioUrl || request.videoUrl || request.fileUrl) && (
            <section className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">첨부 자료</h3>
              <div className="space-y-2">
                {request.audioUrl && (
                  <a
                    href={request.audioUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 text-sm text-blue-700 hover:bg-blue-100"
                  >
                    <FileAudio2 className="w-4 h-4 shrink-0" />
                    <span className="truncate">
                      {request.audioFileName || "음원 파일"}
                    </span>
                    <ExternalLink className="w-3.5 h-3.5 ml-auto shrink-0 opacity-60" />
                  </a>
                )}
                {request.videoUrl && (
                  <a
                    href={request.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-xl border border-purple-100 bg-purple-50 px-3 py-2.5 text-sm text-purple-700 hover:bg-purple-100"
                  >
                    <FileVideo2 className="w-4 h-4 shrink-0" />
                    <span className="truncate">
                      {request.videoFileName || "영상 파일"}
                    </span>
                    <ExternalLink className="w-3.5 h-3.5 ml-auto shrink-0 opacity-60" />
                  </a>
                )}
                {request.fileUrl && (
                  <a
                    href={request.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700 hover:bg-emerald-100"
                  >
                    <Paperclip className="w-4 h-4 shrink-0" />
                    <span className="truncate">
                      {request.attachmentFileName || request.fileName || "첨부 파일"}
                    </span>
                    <ExternalLink className="w-3.5 h-3.5 ml-auto shrink-0 opacity-60" />
                  </a>
                )}
              </div>
            </section>
          )}

          {proId && (
            <section className="rounded-2xl border border-violet-100 bg-violet-50/50 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                배정된 프로 {proName ? `· ${proName}` : ""}
              </h3>
              <ProProfileCard proId={proId} proName={proName} />
            </section>
          )}

          <section className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4 space-y-4">
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-violet-600" />
              <h3 className="text-sm font-semibold text-gray-900">결제 영수증</h3>
            </div>
            {request.paymentStatus === "paid" || paymentId ? (
              <dl className="grid grid-cols-[96px_1fr] gap-y-2.5 text-sm">
                <dt className="text-gray-500">결제 상태</dt>
                <dd className="text-gray-800">
                  {request.paymentStatus === "refunded"
                    ? "환불 완료"
                    : request.paymentStatus === "paid"
                      ? "결제 완료"
                      : request.status === "payment_pending"
                        ? "미결제"
                        : "확인 중"}
                </dd>
                <dt className="text-gray-500">결제 금액</dt>
                <dd className="text-gray-950 font-semibold text-base">
                  {formatAmount(request.paidAmount)}
                </dd>
                {typeof request.originalPrice === "number" &&
                  request.originalPrice !== request.paidAmount && (
                    <>
                      <dt className="text-gray-500">정상가</dt>
                      <dd className="text-gray-400 line-through">
                        {formatAmount(request.originalPrice)}
                      </dd>
                    </>
                  )}
                <dt className="text-gray-500">결제 수단</dt>
                <dd className="text-gray-800 flex items-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5 text-gray-500" />
                  {request.paymentProvider?.toUpperCase() || "PORTONE"}{" "}
                  {request.paymentMethod || "card"}
                </dd>
                <dt className="text-gray-500">결제 번호</dt>
                <dd className="text-gray-700 font-mono text-xs break-all">
                  {paymentId || "-"}
                </dd>
                <dt className="text-gray-500">승인 일시</dt>
                <dd className="text-gray-800">
                  {formatDateTime(request.paymentVerifiedAt)}
                </dd>
                <dt className="text-gray-500">상품명</dt>
                <dd className="text-gray-800">
                  커넥톤 프로 피드백 · {optionLabel}
                </dd>
              </dl>
            ) : (
              <p className="text-sm text-gray-500">
                결제 정보가 없거나 아직 결제가 완료되지 않았습니다.
              </p>
            )}
          </section>

          {request.status === "completed" && request.requestType === "feedback" && (
            <Link
              href={`/feedback/${request.id}`}
              className="flex items-center justify-center gap-2 w-full rounded-xl bg-violet-600 hover:bg-violet-500 text-gray-950 font-semibold py-3 transition"
            >
              피드백 리포트 보기
              <ExternalLink className="w-4 h-4" />
            </Link>
          )}

          {error && (
            <p className="border border-rose-200 bg-rose-50 text-sm text-rose-700 rounded-xl px-3 py-2">
              {error}
            </p>
          )}

          {!cancellable &&
            (request.status === "assigned" ||
              request.status === "in_progress" ||
              !!request.assignedProId ||
              !!request.expertId) &&
            request.status !== "cancelled" &&
            request.status !== "completed" && (
              <p className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                프로가 배정된 이후에는 앱에서 취소할 수 없습니다. 필요하면 1:1
                문의로 요청해 주세요.
              </p>
            )}
        </div>

        {cancellable && (
          <div className="fixed bottom-0 right-0 w-full max-w-lg border-t border-gray-200 bg-white/95 backdrop-blur px-5 py-4">
            <button
              type="button"
              onClick={() => void handleCancel()}
              disabled={cancelling}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 py-3 text-sm font-semibold disabled:opacity-50"
            >
              {cancelling ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Ban className="w-4 h-4" />
              )}
              {refundable ? "신청 취소 및 결제 환불" : "신청 취소"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}